import React, { useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import ThemeToggleButton from './../items/ThemeToggleButton';
import ListItemText from '@mui/material/ListItemText';
import muvikaAttrs from './../../attrs'
import TR from './../../Trans';
import ChooseLanguage from './../items/ChooseLanguage';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import settingsStore from '../../stores/settings';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import version from './../../version';
import { LightMode, DarkMode } from "@mui/icons-material";
import { useLocalStorage } from '../../services/useLocalStorage';
import SearchIcon from '@mui/icons-material/Search';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import navigationStore from './../../stores/navigation';
import songbookSessionStore from './../../stores/songbookSession';
import { Views } from './../../enums/enums';
import environmentStore from '../../stores/environment';
import TermsDialog from '../dialogs/TermsDialog';

function SongbookSideBar(props) {
  const sessionToken = songbookSessionStore((state) => state.sessionToken);
  const session = songbookSessionStore((state) => state.session);
  const isSongbookPublicMode = songbookSessionStore((state) => state.isSongbookPublicMode);
  const isSongbookPublicBoothMode = songbookSessionStore((state) => state.isSongbookPublicBoothMode);
  const goToView = navigationStore((state) => state.goToView);
  const currentView = navigationStore((state) => state.currentView);
  const environment = environmentStore((state) => state.environment);
  const getEnvironment = environmentStore((state) => state.getEnvironment);
  const settings = settingsStore((state) => state.settings);
  const getSettings = settingsStore((state) => state.getSettings);
  const updateSession = songbookSessionStore((state) => state.updateSession);

  const [theme, setTheme] = useLocalStorage(muvikaAttrs.service + "-songbook-theme", "dark");

  const [isPrivacyDialogOpen, setIsPrivacyDialogOpen] = useState(false);
  const [, updateState] = useState();

  const handleOpenPrivacyDialog = () => { 
    setIsPrivacyDialogOpen(true);
  };

  const handleClosePrivacyDialog = () => { 
    setIsPrivacyDialogOpen(false);
  };

  useEffect(() => { 
    goToView(Views.NewTracks);
    if (!environment) {
      getEnvironment(sessionToken);
    }
    if (!settings) {
      getSettings(sessionToken);
    }
  }, []);

  const renderNewTracksLink = () => { 
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.NewTracks)} selected={currentView == Views.NewTracks}>
        <ListItemIcon>
          <FiberNewIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_new} />
      </ListItemButton>
    </ListItem>;
  };

  const renderTracksLink = () => { 
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.Catalog)} selected={currentView == Views.Catalog}>
        <ListItemIcon>
          <MusicNoteIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_browse} />
      </ListItemButton>
    </ListItem>;
  };

  const renderSearchLink = () => { 
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.Search)} selected={currentView == Views.Search}>
        <ListItemIcon>
          <SearchIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_search} />
      </ListItemButton>
    </ListItem>;
  };

  const renderPerformancesLink = () => { 
    return <ListItem disablePadding>
      <ListItemButton onClick={() => goToView(Views.SongbookPerformances)} selected={currentView == Views.SongbookPerformances}>
        <ListItemIcon>
          <QueueMusicIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_playqueue} />
      </ListItemButton>
    </ListItem>;
  };

  const renderPrivacyLink = () => { 
    return <ListItem disablePadding>
      <ListItemButton onClick={handleOpenPrivacyDialog}>
        <ListItemIcon>
          <PrivacyTipIcon />
        </ListItemIcon>
        <ListItemText primary={TR.cm_register_clause} />
      </ListItemButton>
    </ListItem>;
  };

  const handleLanguageChange = (language) => {
    let lowLang = language.toLowerCase();
    updateSession(sessionToken, lowLang);
    updateState({});
  };

  const renderVersion = () => { 
    if (isSongbookPublicMode) {
      return <ListItem disablePadding>
        <ListItemButton className="muvika-version-number">
          <small>{version} Public mode {muvikaAttrs.side==="dev" ? "no_rep" : ""}</small>
        </ListItemButton>
      </ListItem>;
    } else if (isSongbookPublicBoothMode) {
      return <ListItem disablePadding>
        <ListItemButton className="muvika-version-number">
          <small>{version} Booth mode {muvikaAttrs.side==="dev" ? "no_rep" : ""}</small>
        </ListItemButton>
      </ListItem>;
    }
    return <ListItem disablePadding>
      <ListItemButton className="muvika-version-number">
        <small>{version} {muvikaAttrs.side==="dev" ? "no_rep" : ""}</small>
      </ListItemButton>
    </ListItem>;
  };


  const renderTermsDialog = () => { 
    if (session) {
      if (session.account.tags.includes("consumer")) {
        return <TermsDialog
          open={isPrivacyDialogOpen}
          onClose={handleClosePrivacyDialog}
          title={TR.cm_register_clause}
          url={environment ? environment.urls.url_privacy_home : ""}
        />;
      } else {
        return <TermsDialog
          open={isPrivacyDialogOpen}
          onClose={handleClosePrivacyDialog}
          title={TR.cm_register_clause}
          url={environment ? environment.urls.url_privacy_pro : ""}
        />;
      }
    }
  };

  const renderPerformancesList = () => { 
    if (settings) {
      if (settings.sb_allow_playqueue_view == 1 && !isSongbookPublicMode) {
        return <List className="muvika-sidebar-list">
          {renderPerformancesLink()}
        </List>;
      }
    }
  };

  const toggleTheme = () => {
    if (theme === "light") {
      document.documentElement.classList.remove("light-theme");
      setTheme("dark");
    } else {
      document.documentElement.classList.add("light-theme");
      setTheme("light");
    }
  };

  const renderThemeLink = () => {
    if (theme === "dark") {
      return <ListItem disablePadding>
        <ListItemButton onClick={() => toggleTheme()}>
          <ListItemIcon>
            <LightMode />
          </ListItemIcon>
          <ListItemText primary={TR.cm_set_light_theme} />
        </ListItemButton>
      </ListItem>;
    } else {
      return <ListItem disablePadding>
        <ListItemButton onClick={() => toggleTheme()}>
          <ListItemIcon>
            <DarkMode />
          </ListItemIcon>
          <ListItemText primary={TR.cm_set_dark_theme} />
        </ListItemButton>
      </ListItem>;
    }
  };

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{ background: "var(--bg-color) !important", "& .MuiPaper-root": { background: "transparent", maxHeight: "100%" } }}
    >
      {renderTermsDialog()}

      <List className="muvika-sidebar-list" >
        {renderNewTracksLink()}
        {renderTracksLink()}
        {renderSearchLink()}
      </List>

      {renderPerformancesList()}

      <List className="muvika-sidebar-list">
        {renderPrivacyLink()}
        {renderThemeLink()}
        {renderVersion()}
      </List>

      <ChooseLanguage isSongbook={true} isBottom={true} environment={environment} onLanguageChange={handleLanguageChange} />
    </Drawer>
  );
}

export default SongbookSideBar;
